/**
 * Turns an API failure into a message in the user's language.
 *
 * The API returns an English `message` plus a machine-readable `error` code.
 * Showing `message` directly leaks English into every other locale, so the code
 * is what gets translated; the server text is only a last resort for something
 * genuinely unexpected.
 */

/** Fallbacks double as the English copy, so EN works even before i18n loads. */
const FALLBACKS = {
    forbidden: "You do not have access to this project.",
    process_failed: "Could not start processing.",
    already_processing: "Every participant is already being processed.",
    transcription_pending: "Still transcribing voice answers. Try again in a moment.",
    no_responses: "No completed responses to analyse yet.",
    no_answers: "No answered questions to analyse yet.",
    title_required: "Give the questionnaire a title before publishing.",
    consent_required: "Add informed consent text before publishing.",
    questions_required: "Add at least one question before publishing.",
    question_text_required: "Every question needs text before publishing.",
    password_required: "That password is not correct.",
    email_required: "An email address is required.",
    already_answered: "You have already answered this questionnaire.",
    unknown_question: "That question is no longer part of this questionnaire.",
    question_required: "Please answer this question.",
    missing_answers: "Some required questions are unanswered.",
    recording_not_enabled: "Recording is not enabled for this questionnaire.",
    recording_too_large: "The recording reached its maximum size.",
    chunk_store_failed: "Part of the recording could not be saved.",
    no_recording_data: "No recording data was received.",
    out_of_order: "The recording arrived out of order.",
    capacity_reached: "This questionnaire is no longer accepting responses.",
    not_active: "Publish the questionnaire before inviting participants.",
    network: "Could not reach the server. Check your connection and try again.",
};

/**
 * @param {Error} error   the rejected axios error
 * @param {Function} t    i18next translate
 * @param {string} [fallbackKey] translation key used when the server sends no code
 */
export function questionnaireError(error, t, fallbackKey = "Generic.SaveFailed") {
    const data = error?.response?.data;
    const code = data?.error;

    if (code && FALLBACKS[code]) {
        // Interpolate whatever the server sent alongside the code, so counted
        // messages ("still transcribing 3…") stay accurate in every language.
        return t(`Questionnaire.Errors.${code}`, FALLBACKS[code], {
            count: data.pending ?? data.started ?? 0,
        });
    }

    // Laravel validation failures arrive as {message, errors:{field:[...]}}.
    // Those strings are framework English; prefer a generic translated line.
    if (data?.errors) {
        return t("Questionnaire.Errors.validation", "Please check the highlighted fields.");
    }

    if (!error?.response) {
        return t("Questionnaire.Errors.network", FALLBACKS.network);
    }

    return t(fallbackKey, "Could not save.");
}
